import React, { useMemo, useState } from 'react';
import _t from 'counterpart';
import PageLayout from '../../components/PageLayout';
import PaginationTable, { ISorter } from '../../components/PaginationTable';
import { useQueryParam, NumberParam, StringParam } from 'use-query-params';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CLink,
  CBadge,
  CSwitch,
  CNav,
  CNavItem,
  CNavLink,
} from '@coreui/react';
import { useQuery } from 'react-query';
import { fetchMt5Wallets, fetchWallets } from '../../services/BackendService';
import { useAppDispatch, useAppSelector, useLiveAccountsUpdates } from '../../helpers/customHooks';
import WalletsFilter from './WalletsFilter';
import SearchFilter from '../../components/SearchFilter';
import { formatCurrency, formatDateTime, formatPercentage, getFiltersCount } from '../../helpers';
import { shallowEqual } from 'react-redux';
import CIcon from '@coreui/icons-react';
import CustomerWalletPopup from '../customers/CustomerWalletPopup';
import { clearWalletsFilters } from '../../actions';
import { IWallet, IWalletWithBalance, IWalletWithBalanceAndStage, WalletPlatform } from './types';
import Error from '../../components/Error';
import { TradingType } from '../customers/types';
import { calculateMarginLevel } from '../../helpers/calculations';
import { stringify } from 'qs';
import { Permission, UserRole } from '../../reducers/userReducer';
import ExportXLSXButton from '../../components/ExportXLSXButton';
import { WalletType } from '../customers/types';

/**
 * Wintrado columns the MT5 list keeps, in the order the shared table declares them.
 *
 * Balances are included: they come from the mt5-bridge rather than the trading gateway, but they
 * are the same figures and belong in the same columns. Prop funnel and stage stay out — those are
 * Wintrado-gateway concepts an MT5 login has no equivalent for.
 */
const MT5_COLUMN_KEYS = [
  'id',
  'customer',
  'balance',
  'profitLoss',
  'marginLevelPercent',
  'margin',
  'marginFree',
  'equity',
  'currency',
  'leverage',
  'group',
  'type',
];
/** Mirrors the backend's Mt5WalletSortField whitelist. */
const MT5_SORTABLE_KEYS = ['id', 'leverage', 'createdAt'];

const WalletsPage = () => {
  const [editWallet, setEditWallet] = useState<IWallet | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [filter, setFilter] = useQueryParam('filter', StringParam);
  const [orderBy = 'id|DESC', setOrderBy] = useQueryParam('orderBy', StringParam);
  const [onlyShowWalletsWithOpenPositions, setOnlyShowWalletsWithOpenPositions] = useState<boolean>(false);
  // In the URL so the tab survives a refresh or a back-navigation from a wallet.
  const [platformTab = WalletPlatform.Wintrado, setPlatformTab] = useQueryParam('platform', StringParam);
  const isMt5Tab = platformTab === WalletPlatform.MT5;

  const dispatch = useAppDispatch();

  const permissions = useAppSelector((state) => state.user.permissions);
  const roles = useAppSelector((state) => state.user.roles);
  const isAuditAgent = roles.includes(UserRole.AuditAgent);
  const canManageWallets = permissions.includes(Permission.MANAGE_WALLETS);

  const walletFilters = useAppSelector((state) => state.filters.walletsFilters, shallowEqual);

  const walletsItemsPerPageFromLocalStorage = Number(localStorage.getItem('items_per_page_wallets_table') ?? 10);
  const [limit, setLimit] = useState<number>(walletsItemsPerPageFromLocalStorage);
  const offset = Number(page) > 0 ? Number(page) * limit - limit : 0;
  useLiveAccountsUpdates();

  const walletFiltersDTO = {
    currencies: walletFilters.currency.map((c) => c.value),
    leverageFrom: walletFilters.leverageFrom,
    leverageTo: walletFilters.leverageTo,
    isDemo: walletFilters.type?.value,
    groups: walletFilters.groups.map((r) => r.value),
    tradingType: walletFilters.tradingType?.value,
    type: walletFilters.type?.value,
    propFunnel: walletFilters.propFunnel?.value,
    excludePropFunnels: walletFilters.excludePropFunnels.map((pf) => pf.value),
    mode: walletFilters.mode?.value,
  };

  const loadWalletsQuery = useQuery(
    ['all-wallets', filter, page, limit, orderBy, walletFiltersDTO, onlyShowWalletsWithOpenPositions],
    () => fetchWallets(limit, offset, filter || '', orderBy, walletFiltersDTO, onlyShowWalletsWithOpenPositions),
    { keepPreviousData: true }
  );

  // Both lists are always fetched so each tab can show how many accounts match the current
  // filters — an empty tab then reads as "wrong tab" rather than "no such account". The MT5
  // endpoint reads only the filters that can mean something without a gateway account and ignores
  // the rest, so it takes the same DTO rather than a hand-maintained subset of it.
  const loadMt5WalletsQuery = useQuery(
    ['all-mt5-wallets', filter, page, limit, orderBy, walletFiltersDTO],
    () => fetchMt5Wallets(limit, offset, filter || '', orderBy, walletFiltersDTO),
    { keepPreviousData: true }
  );

  const activeQuery = isMt5Tab ? loadMt5WalletsQuery : loadWalletsQuery;

  const onLimitChanged = (limit: number) => {
    setLimit(limit);
    localStorage.setItem('items_per_page_wallets_table', limit.toString());
    // `limit` is in both query keys, so changing it refetches the tab in view. The invalidate this
    // replaced named a key ('wallets') that neither query uses.
  };

  const resetFilters = () => {
    setOnlyShowWalletsWithOpenPositions(false);
    dispatch(clearWalletsFilters());
  };

  const onPageChanged = (page: number) => {
    setPage(page, 'replaceIn');
  };

  const onPlatformTabChanged = (platform: WalletPlatform) => {
    if (platform === platformTab) {
      return;
    }
    // The two lists sort on different columns, so carrying a sort like profitLoss across would
    // leave the header showing a column the other list cannot order by.
    setPlatformTab(platform, 'replaceIn');
    setOrderBy(platform === WalletPlatform.MT5 ? 'createdAt|DESC' : 'id|DESC', 'replaceIn');
    setPage(1, 'replaceIn');
  };

  const onSorterChanged = ({ column, asc }: ISorter) => {
    const sortBy = `${column}|${asc ? 'ASC' : 'DESC'}`;
    if (sortBy !== orderBy) {
      setOrderBy(sortBy, 'replaceIn');
      setPage(1, 'replaceIn');
    }
  };

  const searchTableData = (value: string) => {
    if (value !== filter) {
      setPage(1, 'replaceIn');
      setFilter(value, 'replaceIn');
    }
  };

  const toggleEnabled = () => {
    setPage(1, 'replaceIn');
    const enabled = !onlyShowWalletsWithOpenPositions;
    setOnlyShowWalletsWithOpenPositions(enabled);
  };

  const onEditModalClosed = () => {
    setEditWallet(null);
    activeQuery.refetch();
  };

  const filtersCount = getFiltersCount(walletFilters);

  const onErrorRetry = () => {
    activeQuery.refetch();
  };

  const fixedCellStyle = useMemo(() => {
    return { minWidth: '130px' };
  }, []);

  const getExportLink = (): string => {
    const [sortColumn, sortDirParam] = (orderBy || 'id|DESC').split('|');
    // Note: Export does not support sorting - wallets are exported in createdAt DESC order
    const qs = stringify({
      limit,
      offset,
      filter: filter || '',
      onlyShowWalletsWithOpenPositions,
      // So the export matches the tab it was started from.
      platform: platformTab,
      ...walletFiltersDTO,
    });

    return `${process.env.REACT_APP_API_ROOT}/api/admin/wallets/export/xlsx?${qs}`;
  };

  const tableFields = useMemo(
    () => [
      { key: 'id', label: _t('wallets.accountId'), sorter: true },
      { key: 'customer', label: _t('wallets.customer-name'), sorter: false },
      { key: 'balance', label: _t('customer.wallets.balance'), sorter: true, _style: fixedCellStyle },
      { key: 'profitLoss', label: _t('wallet.profit-loss'), sorter: true, _style: fixedCellStyle },
      { key: 'marginLevelPercent', label: _t('wallet.margin-level'), sorter: true, _style: fixedCellStyle },
      { key: 'margin', label: _t('wallet.margin'), sorter: true, _style: fixedCellStyle },
      { key: 'marginFree', label: _t('wallet.free-margin'), sorter: true, _style: fixedCellStyle },
      { key: 'equity', label: _t('wallet.equity'), sorter: true, _style: fixedCellStyle },
      { key: 'currency', label: _t('wallets.currency'), sorter: false },
      { key: 'leverage', label: _t('wallets.leverage'), sorter: false },
      { key: 'group', label: _t('wallets.group'), sorter: false },
      { key: 'propFunnel', label: _t('wallets.prop-funnel'), sorter: false },
      { key: 'stage', label: _t('wallets.stage'), sorter: false },
      { key: 'type', label: _t('wallets.type'), sorter: false },
      { key: 'tradingType', label: _t('wallets.trading-type'), sorter: false },
      { key: 'isDemo', label: _t('wallet-modal.is-demo'), sorter: false },
      { key: 'actions', label: _t('global.actions'), sorter: false },
    ],
    [fixedCellStyle]
  );

  const scopedSlots = useMemo(
    () => ({
      id: (wallet: IWalletWithBalance) => (
        <td>
          <CLink href={`/wallets/${wallet.id}`}>{wallet.id}</CLink>
        </td>
      ),
      isDemo: (wallet: IWalletWithBalance) => (
        <td>
          {wallet.type === WalletType.Affiliate ? (
            '-'
          ) : (
            <CBadge color={wallet.isDemo ? 'danger' : 'success'}>
              {wallet.isDemo ? _t('global.demo') : _t('global.real')}
            </CBadge>
          )}
        </td>
      ),
      tradingType: (wallet: IWalletWithBalance) => (
        <td>
          {wallet.type === WalletType.Affiliate ? (
            '-'
          ) : (
            <CBadge style={{ backgroundColor: wallet.tradingType === TradingType.Netting ? '#18B8C2' : '#9618C2' }}>
              {wallet.tradingType === TradingType.Netting ? _t('wallets.netting') : _t('wallets.hedging')}
            </CBadge>
          )}
        </td>
      ),
      balance: (wallet: IWalletWithBalance) => <td>{formatCurrency(wallet.balance, wallet.currency)}</td>,
      marginLevelPercent: (wallet: IWalletWithBalance) => (
        <td>{wallet.type === WalletType.Affiliate ? '-' : formatPercentage(wallet.marginLevelPercent)}</td>
      ),
      margin: (wallet: IWalletWithBalance) => (
        <td>{wallet.type === WalletType.Affiliate ? '-' : formatCurrency(wallet.margin, wallet.currency)}</td>
      ),
      marginFree: (wallet: IWalletWithBalance) => (
        <td>{wallet.type === WalletType.Affiliate ? '-' : formatCurrency(wallet.marginFree, wallet.currency)}</td>
      ),
      equity: ({ equity, currency }: IWalletWithBalance) => <td>{formatCurrency(equity, currency)}</td>,
      profitLoss: (wallet: IWalletWithBalance) => (
        <td>{wallet.type === WalletType.Affiliate ? '-' : formatCurrency(wallet.profitLoss, wallet.currency)}</td>
      ),
      customer: (wallet: IWalletWithBalance) => (
        <td>
          <CLink href={`../customers/${wallet.customer?.id}`}>{wallet.customer?.name}</CLink>
        </td>
      ),
      leverage: (wallet: IWalletWithBalance) => <td>{wallet.type === WalletType.Affiliate ? '-' : wallet.leverage}</td>,
      group: (wallet: IWalletWithBalance) => (
        <td>
          {wallet.type === WalletType.Affiliate ? (
            '-'
          ) : (
            <>{isAuditAgent ? (wallet.group?.name ?? '-') : <CLink href={`/groups/${wallet.group?.id}`}>{wallet.group?.name}</CLink>}</>
          )}
        </td>
      ),
      propFunnel: (wallet: IWalletWithBalance) => (
        <td>
          {wallet.propFunnelId ? (
            <>{isAuditAgent ? wallet.propFunnelId : <CLink href={`/prop-funnels/${wallet.propFunnelId}`}>{wallet.propFunnelId}</CLink>}</>
          ) : (
            '-'
          )}
        </td>
      ),
      stage: (wallet: IWalletWithBalanceAndStage) => (
        <td>{wallet.currentStage ? `${wallet.currentStage}/${wallet.maxStage}` : '-'}</td>
      ),
      type: (wallet: IWalletWithBalance) => <td>{wallet.type}</td>,
      actions: (wallet: IWalletWithBalance) => (
        <td>
          {wallet.type !== WalletType.Affiliate && canManageWallets && (
            <CButton
              className="mr-2"
              color="primary"
              type="button"
              onClick={() => setEditWallet(wallet)}
              title="Edit wallet"
            >
              <CIcon name="cil-pencil" size="sm" />
            </CButton>
          )}
        </td>
      ),
    }),
    [canManageWallets, isAuditAgent]
  );

  /**
   * The MT5 list is the Wintrado table minus the columns that mean nothing for an MT5 login (prop
   * funnel, stage) plus status and creation date, so it reuses the same slots and overrides only
   * what genuinely differs. Sortable columns mirror the backend whitelist — this page is ordered
   * by Postgres, and balances are not stored there.
   */
  const mt5TableFields = useMemo(
    () => [
      ...tableFields
        .filter((field) => MT5_COLUMN_KEYS.includes(field.key))
        .map((field) => ({ ...field, sorter: MT5_SORTABLE_KEYS.includes(field.key) })),
      { key: 'status', label: _t('global.status'), sorter: false },
      { key: 'createdAt', label: _t('global.created-at'), sorter: true },
      { key: 'actions', label: _t('global.actions'), sorter: false },
    ],
    [tableFields]
  );

  const mt5ScopedSlots = useMemo(
    () => ({
      ...scopedSlots,
      // Not a link: MT5 groups have no detail page, since everything on it belongs to the gateway.
      group: (wallet: IWallet) => <td>{wallet.group?.name ?? '-'}</td>,
      status: (wallet: IWallet) => (
        <td>
          <CBadge color={wallet.status.toLowerCase()}>{wallet.status}</CBadge>
        </td>
      ),
      createdAt: (wallet: IWallet) => <td>{wallet.createdAt ? formatDateTime(wallet.createdAt) : '-'}</td>,
    }),
    [scopedSlots]
  );

  if (activeQuery.isError) {
    return <Error onRetry={onErrorRetry} />;
  }

  return (
    <PageLayout title={_t.translate('wallets.title')}>
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader className="pb-0">
              <div>
                <div className="filters-header">
                  <SearchFilter onSearch={searchTableData} />
                  <div className="filters-header-inline w-100">
                    <div className="filters-header-buttons float-left">
                      <CButton className="filters-header-buttons-active" onClick={() => setShowFilters(!showFilters)}>
                        <div className="d-flex justify-content-center align-items-center">
                          <span>{_t('global.filters')}</span>
                          {filtersCount > 0 && (
                            <div className="filters-header-buttons-active-inner">{filtersCount}</div>
                          )}
                          <div className={`filters-header-buttons-active-image ${showFilters ? 'rotated' : ''}`} />
                        </div>
                      </CButton>
                      <CButton onClick={resetFilters} className="filters-header-buttons-reset">
                        {_t('action.reset')}
                      </CButton>
                    </div>
                  </div>
                </div>
                <WalletsFilter show={showFilters} />
              </div>
              <CNav variant="tabs" className="mt-3">
                <CNavItem>
                  <CNavLink active={!isMt5Tab} onClick={() => onPlatformTabChanged(WalletPlatform.Wintrado)}>
                    {_t('wallets.tab-wintrado')} ({loadWalletsQuery.data?.total ?? 0})
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink active={isMt5Tab} onClick={() => onPlatformTabChanged(WalletPlatform.MT5)}>
                    {_t('wallets.tab-mt5')} ({loadMt5WalletsQuery.data?.total ?? 0})
                  </CNavLink>
                </CNavItem>
              </CNav>
              <div style={{ marginTop: '1%' }}>
                {/* The open-positions switch is gateway-backed — positions come from the Wintrado
                    gateway, which holds nothing for an MT5 login — so it stays on that tab. The
                    export reads the local wallet table and answers for either platform. */}
                {!isMt5Tab && (
                  <>
                    <CSwitch color="primary" checked={onlyShowWalletsWithOpenPositions} onChange={toggleEnabled} />
                    <span className="ml-2">
                      {_t.translate('customer.wallets.only-show-wallets-with-open-positions')}
                    </span>
                  </>
                )}
                <div className="float-right">
                  {permissions.includes(Permission.VIEW_WALLETS) && (
                    <ExportXLSXButton downloadLink={getExportLink()} defaultFilename="wallets.xlsx" />
                  )}
                </div>
              </div>
            </CCardHeader>
            <CCardBody>
              <PaginationTable
                tableFields={isMt5Tab ? mt5TableFields : tableFields}
                scopedSlots={isMt5Tab ? mt5ScopedSlots : scopedSlots}
                data={activeQuery.data?.wallets || []}
                onPageChanged={onPageChanged}
                onSorterChanged={onSorterChanged}
                sorter={(() => {
                  const [sortColumn, sortDir] = (orderBy || 'id|DESC').split('|');
                  return { column: sortColumn, asc: sortDir === 'ASC' };
                })()}
                loading={activeQuery.isLoading}
                pages={activeQuery.data?.pages || 0}
                activePage={page || 1}
                pagination
                onLimitChanged={onLimitChanged}
                itemsPerPage={limit}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {editWallet && <CustomerWalletPopup wallet={editWallet} onClose={onEditModalClosed} />}
    </PageLayout>
  );
};

export default React.memo(WalletsPage);
